import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Copy, Pause, Pencil, Play, Plus, Ticket, Users } from 'lucide-react';
import { EtiquetaDePagina, CLASE_TITULO_DE_PAGINA } from '@/components/patterns';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ToastAction } from '@/components/ui/toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/lib/hooks/use-toast';
import { copyToClipboard } from '@/lib/utils/clipboard';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { adminService } from '../services/admin-service';
import type { EstadoDeInvitacionDemo, InvitacionDemo } from '../types/admin.types';
import { InvitacionDemoDialog } from '../components/invitacion-demo-dialog';
import { AltasDeInvitacionDialog } from '../components/altas-de-invitacion-dialog';

const ESTADO: Record<
  EstadoDeInvitacionDemo,
  { rotulo: string; variante: 'success' | 'secondary' | 'warning' | 'info' }
> = {
  ACTIVA: { rotulo: 'Activa', variante: 'success' },
  PAUSADA: { rotulo: 'En pausa', variante: 'secondary' },
  VENCIDA: { rotulo: 'Vencida', variante: 'warning' },
  AGOTADA: { rotulo: 'Agotada', variante: 'info' },
};

function textoDeAltas(i: InvitacionDemo): string {
  return i.topeDeAltas === null
    ? `${i.altasUsadas} · sin tope`
    : `${i.altasUsadas} de ${i.topeDeAltas}`;
}

/** Plataforma › Invitaciones: los enlaces con los que los comercios entran a la demo. */
export function AdminInvitacionesDemoPage() {
  const { toast } = useToast();
  const [invitaciones, setInvitaciones] = useState<InvitacionDemo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [errorAlCargar, setErrorAlCargar] = useState(false);
  const [intento, setIntento] = useState(0);
  // `undefined` = diálogo cerrado; `null` = nueva; una invitación = se edita.
  const [editando, setEditando] = useState<InvitacionDemo | null | undefined>(undefined);
  const [verAltas, setVerAltas] = useState<InvitacionDemo | null>(null);
  const [trabajando, setTrabajando] = useState<string | null>(null);

  const cargar = useCallback(() => {
    return adminService
      .getInvitacionesDemo()
      .then((data) => {
        setInvitaciones(data);
        setErrorAlCargar(false);
      })
      .catch(() => setErrorAlCargar(true))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    setCargando(true);
    void cargar();
  }, [cargar, intento]);

  const copiarEnlace = async (inv: InvitacionDemo, ofrecerBoton = false) => {
    const enlace = `${window.location.origin}/invitacion/${inv.codigo}`;
    // 3-nov: en http (DEV por IP) el navegador no deja usar navigator.clipboard;
    // copyToClipboard tiene el camino de respaldo.
    if (await copyToClipboard(enlace)) {
      toast({ title: 'Enlace copiado' });
    } else {
      toast({
        variant: 'destructive',
        title: 'No se pudo copiar el enlace',
        description: ofrecerBoton
          ? `Toca «Copiar» para intentarlo de nuevo, o cópialo a mano: ${enlace}`
          : `Cópialo a mano: ${enlace}`,
        // Tras crear, el navegador pudo haber perdido el gesto de la persona (hubo una
        // espera de red): un botón nuevo le devuelve un clic propio.
        ...(ofrecerBoton
          ? {
              action: (
                <ToastAction altText="Copiar el enlace" onClick={() => void copiarEnlace(inv)}>
                  Copiar
                </ToastAction>
              ),
            }
          : {}),
      });
    }
  };

  const pausarOReactivar = async (inv: InvitacionDemo) => {
    const pausar = inv.estado !== 'PAUSADA';
    setTrabajando(inv.id);
    try {
      await adminService.editarInvitacionDemo(inv.id, { pausada: pausar });
      await cargar();
      toast({ title: pausar ? 'Invitación en pausa' : 'Invitación reactivada' });
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'No se pudo guardar',
        description: mensajeDeError(e, 'No se pudo guardar. Probá de nuevo.'),
      });
    } finally {
      setTrabajando(null);
    }
  };

  const alGuardar = (guardada: InvitacionDemo) => {
    const eraNueva = editando === null;
    setEditando(undefined);
    void cargar();
    if (eraNueva) {
      // Recién creada: el enlace queda copiado, listo para repartir.
      toast({ title: 'Invitación creada' });
      void copiarEnlace(guardada, true);
    } else {
      toast({ title: 'Cambios guardados' });
    }
  };

  return (
    <AppLayout variante="plataforma">
      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <Ticket className="mt-1 h-6 w-6" />
            <div>
              <div className="mb-3">
                <EtiquetaDePagina tono="neutro">Plataforma</EtiquetaDePagina>
              </div>
              <h1 className={CLASE_TITULO_DE_PAGINA}>Invitaciones de la demo</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Cada comercio entra a la demo con un enlace. Desde acá los creás, los pausás y ves
                quién se dio de alta.
              </p>
            </div>
          </div>
          <Button onClick={() => setEditando(null)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Nueva invitación
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            {cargando ? (
              <div className="space-y-3 p-6">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : errorAlCargar ? (
              <div className="flex flex-col items-center gap-3 p-10 text-center">
                <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden="true" />
                <div>
                  <p className="font-medium">No se pudieron cargar las invitaciones</p>
                  <p className="text-sm text-muted-foreground">
                    Puede ser un problema de conexión. Probá de nuevo.
                  </p>
                </div>
                <Button variant="outline" onClick={() => setIntento((n) => n + 1)}>
                  Reintentar
                </Button>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Enlace</TableHead>
                      <TableHead>Altas</TableHead>
                      <TableHead>Vence</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invitaciones.map((inv) => {
                      const estado = ESTADO[inv.estado];
                      const enPausa = inv.estado === 'PAUSADA';
                      return (
                        <TableRow key={inv.id}>
                          <TableCell className="font-medium">{inv.nombre}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <code className="text-xs">{inv.codigo}</code>
                              <Button
                                variant="outline"
                                size="sm"
                                aria-label={`Copiar enlace de ${inv.nombre}`}
                                onClick={() => void copiarEnlace(inv)}
                              >
                                <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                                Copiar enlace
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap tabular-nums">
                            {textoDeAltas(inv)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm">
                            {inv.venceEn ? (
                              new Date(inv.venceEn).toLocaleDateString('es-BO')
                            ) : (
                              <span className="text-muted-foreground">No vence</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={estado.variante}>{estado.rotulo}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1.5">
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={trabajando === inv.id}
                                aria-label={`${enPausa ? 'Reactivar' : 'Pausar'} ${inv.nombre}`}
                                onClick={() => void pausarOReactivar(inv)}
                              >
                                {enPausa ? (
                                  <Play className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                                ) : (
                                  <Pause className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                                )}
                                {enPausa ? 'Reactivar' : 'Pausar'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label={`Editar ${inv.nombre}`}
                                onClick={() => setEditando(inv)}
                              >
                                <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                                Editar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label={`Ver altas de ${inv.nombre}`}
                                onClick={() => setVerAltas(inv)}
                              >
                                <Users className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                                Ver altas
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {invitaciones.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                          Todavía no creaste ninguna invitación.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <InvitacionDemoDialog
        abierto={editando !== undefined}
        invitacion={editando ?? null}
        onCerrar={() => setEditando(undefined)}
        onGuardada={alGuardar}
      />
      <AltasDeInvitacionDialog invitacion={verAltas} onCerrar={() => setVerAltas(null)} />
    </AppLayout>
  );
}
